import { useEffect, useState } from "react";
import { AccessibilityInfo } from "react-native";

export interface ResolvedReducedMotion {
    /** True once the AccessibilityInfo API has answered (or pushed an update). */
    resolved: boolean;
    /** The current reduce-motion preference. Meaningless until `resolved` is true. */
    reduced: boolean;
}

/**
 * Returns `{ resolved, reduced }`. On first render `resolved=false, reduced=false`.
 * After the async `AccessibilityInfo.isReduceMotionEnabled()` promise resolves,
 * returns `{ resolved: true, reduced: <actual> }`. Subsequent system changes
 * update via the `reduceMotionChanged` event.
 *
 * **The `resolved` flag is the whole point.** Consumers must NOT initialize
 * a long-lived value (e.g. `useRef(new Animated.Value(reduced ? ...))`) using
 * `reduced` while `resolved === false` — that captures the placeholder value
 * permanently. Gate render on `resolved`, then create derived state.
 */
export function useResolvedReducedMotion(): ResolvedReducedMotion {
    const [state, setState] = useState<ResolvedReducedMotion>({
        resolved: false,
        reduced: false
    });

    useEffect(() => {
        let alive = true;
        AccessibilityInfo.isReduceMotionEnabled()
            .then((v) => {
                if (alive) {
                    setState({ resolved: true, reduced: v });
                }
            })
            .catch(() => {
                // API unavailable on this platform; declare resolved=true with the
                // safe default so consumers proceed instead of hanging.
                if (alive) {
                    setState({ resolved: true, reduced: false });
                }
            });
        const sub = AccessibilityInfo.addEventListener("reduceMotionChanged", (v: boolean) => {
            if (alive) {
                setState({ resolved: true, reduced: v });
            }
        });
        return () => {
            alive = false;
            sub.remove();
        };
    }, []);

    return state;
}
